import { useEffect, useRef, useState, type MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../../assets/images/educator-logo.svg";
import { BottomHeaderNav } from "./BottomHeaderNav";
import "./Header.scss";
import { useUserContext } from "../../../stores/userContext";
import { buildMessagesPath, isMessagesPathname } from "../../../helpers/Messages/chatHelper";
import { useLocalization } from "../../../stores/localizationContext";

type LanguageValue = "en" | "ar";
const LANGUAGE_OPTIONS: { label: string; value: LanguageValue }[] = [
  { label: "English", value: "en" },
  { label: "العربية", value: "ar" },
];

export const Header = () => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLLIElement>(null);
  const langDropdownRef = useRef<HTMLLIElement>(null);
  const { user } = useUserContext();
  const { t, lang, setLanguage } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();

  const displayName = user?.firstName || user?.email || "";
  const cdnBase = (import.meta as any).env?.VITE_CDN_BASE;
  const profileImg =
    (user as any)?.profileImage ||
    `${cdnBase}default-profile.svg`;

  const handleClickOutside = (event: globalThis.MouseEvent) => {
    // Close profile dropdown if clicking outside
    if (
      profileDropdownRef.current &&
      !profileDropdownRef.current.contains(event.target as Node)
    ) {
      setProfileOpen(false);
    }
    // Close language dropdown if clicking outside
    if (
      langDropdownRef.current &&
      !langDropdownRef.current.contains(event.target as Node)
    ) {
      setLangOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setProfileOpen(!profileOpen);
    setLangOpen(false); // Close language dropdown when opening profile
  };

  const handleLangClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setLangOpen(!langOpen);
    setProfileOpen(false); // Close profile dropdown when opening language
  };

  const isAdminActive = location.pathname.includes("/admin");

  /** Home route is `/:language` — sessions tab should highlight there */
  const pathWithoutTrailingSlash =
    location.pathname.replace(/\/$/, "") || "/";
  const isSessionsActive = pathWithoutTrailingSlash === `/${lang}`;

  const messagesPath = buildMessagesPath(lang);
  const isMessagesActive = isMessagesPathname(location.pathname, lang);

  const navLinks = [
    {
      label: t("sessions_app_name"),
      href: `/${lang}`,
      icon: "screen-users-icon",
      active: isSessionsActive,
    },
    {
      label: t("messages_tab_name"),
      href: messagesPath,
      icon: "comments-icon",
      active: isMessagesActive,
      onClick: (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate(messagesPath);
      },
    },
  ];

  const adminLink = {
    label: t("admin_tab"),
    href: `/${lang}/admin/`,
    icon: "admin",
    active: isAdminActive,
  };

  const onSelectLanguage = (target: LanguageValue) => {
    if (target !== "en" && target !== "ar") return;
    // Update provider state
    setLanguage(target);
    setLangOpen(false);
    // Update URL prefix to reflect selected language without full reload
    const pathname = location.pathname || "/";
    const parts = pathname.split("/").filter(Boolean);
    const first = parts[0];
    const isLangPrefixed = first === "en" || first === "ar";
    let newPath = "";
    if (isLangPrefixed) {
      parts[0] = target;
      newPath = "/" + parts.join("/");
    } else {
      newPath = `/${target}${pathname === "/" ? "" : pathname}`;
    }
    navigate(newPath + location.search + location.hash, { replace: true });
  };

  return (
    <header className="main-header">
      <div className="top-header">
        <div className="container">
          <div className="logo">
            <a href={`/${lang}`} title="Nagwa">
              <img src={logo} alt="Nagwa Logo" />
            </a>
          </div>
          <div className="user-actions">
            <ul>
              <li className="dropdown" ref={profileDropdownRef}>
                <a href="#" onClick={handleProfileClick}>
                  <div className="account-pic">
                    <img alt={`${displayName} profile`} src={profileImg} />
                  </div>
                  <span>{displayName}</span>
                  <i className="f-icon chevron-down-icon"></i>
                </a>
                {profileOpen && (
                  <ul className="dropdown-menu user">
                    <li>
                      <a href={`/${lang}/profile/`}>
                        <div className="icon">
                          <img
                            src={`${cdnBase}profile-icon.svg`}
                            alt={t("profile_label")}
                          />
                        </div>
                        {t("profile_label")}
                      </a>
                    </li>

                    <li>
                      <a href={`/${lang}/signinemail/`}>
                        <div className="icon">
                          <img
                            src={`${cdnBase}sign-out-icon.svg`}
                            alt={t("sign_out_label")}
                          />
                        </div>
                        {t("sign_out_label")}
                      </a>
                    </li>
                  </ul>
                )}
              </li>
              <li className="dropdown" ref={langDropdownRef}>
                <a href="#" onClick={handleLangClick}>
                  <i className="f-icon globe-icon"></i>
                  <span>{(lang as LanguageValue) === "ar" ? t("language_ar_label") : t("language_en_label")}</span>
                  <i className="f-icon chevron-down-icon"></i>
                </a>
                {langOpen && (
                  <ul className="dropdown-menu languages">
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <li key={opt.value}>
                        <a
                          href="#"
                          tabIndex={-1}
                          onClick={(e) => {
                            e.preventDefault();
                            onSelectLanguage(opt.value);
                          }}
                        >
                          {opt.value === "ar" ? t("language_ar_label") : t("language_en_label")}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <BottomHeaderNav navLinks={navLinks} adminLink={adminLink} />
    </header>
  );
};
